import {createReducer, on} from '@ngrx/store';
import {createEntityAdapter, EntityAdapter, EntityState} from '@ngrx/entity';
import {IDiagnosis} from '../_interface/diagnosis.model';
import * as DiagnosisActions from '../_actions/diagnosis.actions';
import {IDiagnosisIdNode} from '../_interface/diagnosisid-node.interface';
import {recreateTree} from '../_fn/recreateTree.fn';
import {removeNode} from '../_fn/removeNode.fn';
import {cloneDeep} from 'lodash-es';

export const diagnosisFeatureKey = 'diagnosis';

export interface DiagnosisState extends EntityState<IDiagnosis> {
	loaded: boolean;
	tree: IDiagnosisIdNode;
	flatTree: Record<string, string[]>;
	filter: boolean;
	favourites: IDiagnosis[];
}

export const adapter: EntityAdapter<IDiagnosis> = createEntityAdapter<IDiagnosis>(
	{selectId: (model) => model.diagnosisId}
);

export const initialState: DiagnosisState = adapter.getInitialState({
	loaded: false,
	tree: {},
	flatTree: {},
	filter: false,
	favourites: [],
});

export const reducer = createReducer(
	initialState,
	on(
		DiagnosisActions.upsertMultipleDiagnosisSuccess,
		(state, { diagnosis, tree, flatTree }) => {
			return adapter.upsertMany(diagnosis, {
				...state,
				tree,
				flatTree,
			});
		}
	),
	on(
		DiagnosisActions.loadDiagnosisSuccess,
		(state, { diagnosis, tree, flatTree }) => {
			return adapter.setAll(diagnosis, {
				...state,
				tree,
				flatTree,
				loaded: true,
			});
		}
	),
	on(DiagnosisActions.addDiagnosis, (state, { diagnosis }) => {
		const { tree, flatTree } = recreateTree(
			diagnosis,
			state.tree,
			state.flatTree
		);
		const result = { ...state, flatTree, tree };
		return adapter.addOne(diagnosis, result);
	}),
	on(DiagnosisActions.updateDiagnosis, (state, { diagnosis }) => {
		const { entities } = state;
		return {
			...state,
			entities: {
				...entities,
				[diagnosis.diagnosisId]: { ...diagnosis },
			},
		};
	}),
	on(DiagnosisActions.filterDisabledDiagnosis, (state, { filter }) => {
		return {
			...state,
			filter,
		};
	}),
	on(DiagnosisActions.toggleFavouriteSuccess, (state, { diagnosisId }) => {
		let diagnosis = state.entities[diagnosisId];
		diagnosis = { ...diagnosis, favourites: !diagnosis.favourites };
		let favourites = [...state.favourites];
		if (!diagnosis.favourites) {
			favourites = favourites.filter(
				(d) => d.diagnosisId !== diagnosis.diagnosisId
			);
		}
		return {
			...state,
			entities: { ...state.entities, [diagnosisId]: diagnosis },
			favourites,
		};
	}),
	on(DiagnosisActions.loadFavouritesSuccess, (state, { favourites }) => ({
		...state,
		favourites,
	})),
	on(DiagnosisActions.deleteDiagnosisSuccess, (state, { id }) => {
		const diagnosis = state.entities[id];
		const parent = state.entities[diagnosis.parentId];
		const { tree, flatTree } = state;
		const newTree = cloneDeep(tree);
		const newFlatTree = cloneDeep(flatTree);
		delete newFlatTree[id];
		removeNode(newTree, id, parent);
		return adapter.removeOne(id, {
			...state,
			tree: newTree,
			flatTree: newFlatTree,
		});
	})
);

export const { selectIds, selectEntities, selectAll, selectTotal } =
	adapter.getSelectors();
